import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
} from "react";
import clsx from "clsx";
import { GdRichTextToolbarButton } from "@/components/ui/GdRichText";
import {
  gdRichTextFromDisplayElement,
  gdRichTextToDisplayHtml,
  type GdRichTextFormat,
} from "@/lib/gdRichText";

export interface GdRichTextInsertHandle {
  /** Text an der gespeicherten Cursorposition einfügen. */
  insertText: (text: string, options?: { asBlock?: boolean }) => void;
  focus: () => void;
}

export type GdRichTextVariant = "default" | "documentLangtext" | "documentBody";

interface GdRichTextEditableSurfaceProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  multiline?: boolean;
  minRows?: number;
  className?: string;
  placeholder?: string;
  id?: string;
  variant?: GdRichTextVariant;
  "aria-label"?: string;
}

export const GdRichTextEditableSurface = forwardRef<
  GdRichTextInsertHandle,
  GdRichTextEditableSurfaceProps
>(function GdRichTextEditableSurface(
  {
    value,
    onChange,
    disabled,
    multiline = true,
    minRows = 2,
    className,
    placeholder,
    id,
    variant = "default",
    "aria-label": ariaLabel,
  },
  ref,
) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isFocusedRef = useRef(false);
  const domSyncedRef = useRef(false);
  const lastMarkdownRef = useRef(String(value ?? ""));
  const savedRangeRef = useRef<Range | null>(null);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el || disabled) return;
    const markdown = gdRichTextFromDisplayElement(el, multiline);
    if (markdown === lastMarkdownRef.current) return;
    lastMarkdownRef.current = markdown;
    onChange?.(markdown);
  }, [disabled, multiline, onChange]);

  const saveSelection = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;
    savedRangeRef.current = range.cloneRange();
  }, []);

  const restoreSelection = useCallback(() => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel) return false;
    el.focus();
    if (savedRangeRef.current) {
      try {
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
        return true;
      } catch {
        // Range ungültig nach DOM-Sync
      }
    }
    // Fallback: Cursor ans Ende
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editorRef.current?.focus();
      },
      insertText: (text: string, options?: { asBlock?: boolean }) => {
        const el = editorRef.current;
        if (!el || disabled || !text) return;
        restoreSelection();
        // asBlock: eigener Absatz an der Cursorposition
        const payload = options?.asBlock ? `\n\n${text}` : text;
        document.execCommand("insertText", false, payload);
        emitChange();
        saveSelection();
      },
    }),
    [disabled, emitChange, restoreSelection, saveSelection],
  );

  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el || isFocusedRef.current) return;
    const next = String(value ?? "");
    if (domSyncedRef.current && next === lastMarkdownRef.current) return;
    lastMarkdownRef.current = next;
    el.innerHTML = gdRichTextToDisplayHtml(next, multiline);
    domSyncedRef.current = true;
    savedRangeRef.current = null;
  }, [multiline, value]);

  const applyFormat = useCallback(
    (format: GdRichTextFormat) => {
      const el = editorRef.current;
      if (!el || disabled) return;
      el.focus();
      restoreSelection();
      const command =
        format === "bold" ? "bold" : format === "italic" ? "italic" : "underline";
      document.execCommand(command, false);
      emitChange();
      saveSelection();
    },
    [disabled, emitChange, restoreSelection, saveSelection],
  );

  function handleFocus() {
    isFocusedRef.current = true;
  }

  function handleBlur(_event: FocusEvent<HTMLDivElement>) {
    saveSelection();
    isFocusedRef.current = false;
    emitChange();
  }

  function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    emitChange();
    saveSelection();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Enter") return;
    if (!multiline) {
      event.preventDefault();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    event.preventDefault();
    const inserted = document.execCommand("insertLineBreak");
    if (!inserted) {
      document.execCommand("insertHTML", false, "<br>");
    }
    emitChange();
    saveSelection();
  }

  return (
    <div
      className={clsx(
        "gd-rich-text",
        variant === "documentLangtext" && "gd-rich-text--document-langtext",
        variant === "documentBody" && "gd-rich-text--document-body",
      )}
    >
      <div
        className="gd-rich-text__toolbar"
        role="toolbar"
        aria-label="Textformatierung"
        onMouseDown={(event) => event.preventDefault()}
      >
        <GdRichTextToolbarButton
          label="B"
          title="Fett"
          activeClassName="font-bold"
          disabled={disabled}
          onClick={() => applyFormat("bold")}
        />
        <GdRichTextToolbarButton
          label="I"
          title="Kursiv"
          activeClassName="italic"
          disabled={disabled}
          onClick={() => applyFormat("italic")}
        />
        <GdRichTextToolbarButton
          label="U"
          title="Unterstrichen"
          activeClassName="underline"
          disabled={disabled}
          onClick={() => applyFormat("underline")}
        />
      </div>
      <div
        id={id}
        ref={editorRef}
        className={clsx(
          "gd-rich-text__editor eh-input",
          !multiline && "gd-rich-text__editor--single",
          disabled && "gd-rich-text__editor--disabled",
          className,
        )}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline={multiline}
        data-placeholder={placeholder}
        style={
          multiline
            ? ({ ["--gd-rich-text-min-rows" as string]: String(minRows) } as const)
            : undefined
        }
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        onMouseDown={(event) => event.stopPropagation()}
        onInput={() => {
          emitChange();
          saveSelection();
        }}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
});
